import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  Stack,
} from '@mui/material'
import {
  Add,
  Visibility,
  Assignment,
  CheckCircle,
  Person,
  ReportProblem,
  FilterList,
  Clear,
} from '@mui/icons-material'
import { PageHeader, Card, Button, DataTable, Modal, StatusChip, ExportButtons, AsyncSearchSelect } from '@components/common'
import { FaultReportForm } from '@components/assets'
import { useAuth } from '@context/AuthContext'
import api from '@services/api'
import { toast } from 'react-toastify'
import { formatDate, formatDateTime } from '@utils/dateUtils'

const priorityColors = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'success',
}

// Status progress mapping - defines the workflow stages
const STATUS_PROGRESS = {
  reported: { value: 10, label: 'Reported', color: '#2196F3' },
  acknowledged: { value: 30, label: 'Acknowledged', color: '#9C27B0' },
  assigned: { value: 50, label: 'Assigned', color: '#FF9800' },
  work_order_created: { value: 60, label: 'Work Order Created', color: '#00BCD4' },
  in_progress: { value: 70, label: 'In Progress', color: '#FF9800' },
  resolved: { value: 100, label: 'Resolved', color: '#4CAF50' },
  closed: { value: 100, label: 'Closed', color: '#607D8B' },
  cancelled: { value: 0, label: 'Cancelled', color: '#F44336' },
}

// Progress Bar Component
const StatusProgressBar = ({ status }) => {
  const progress = STATUS_PROGRESS[status] || { value: 0, label: status, color: '#999' }

  return (
    <Box sx={{ width: '100%', minWidth: 120 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="caption" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
          {progress.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {progress.value}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress.value}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            bgcolor: progress.color,
          },
        }}
      />
    </Box>
  )
}

const FaultReportsList = () => {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('fault_reports.manage')
  const canCreate = hasPermission('fault_reports.create')

  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [createWOModalOpen, setCreateWOModalOpen] = useState(false)
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [reportFormOpen, setReportFormOpen] = useState(false)
  const [selectedReport, setSelectedReport] = useState(null)
  const [assignedTo, setAssignedTo] = useState('')
  const [saving, setSaving] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  // Pagination state
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
  })

  // Refs for stable callbacks
  const filtersRef = useRef(filters)
  const searchTermRef = useRef(searchTerm)
  const pageSizeRef = useRef(pageSize)

  // Work Order form fields
  const [woFormData, setWoFormData] = useState({
    title: '',
    description: '',
    priority: '',
    category: 'biomedical',
    work_type: 'corrective',
    assigned_to: '',
    due_date: '',
  })

  // Export columns
  const exportColumns = useMemo(() => [
    { field: 'report_number', headerName: 'Report #' },
    { field: 'title', headerName: 'Fault Description' },
    { field: 'asset_name', headerName: 'Item' },
    { field: 'asset_tag', headerName: 'Asset Tag' },
    { field: 'priority', headerName: 'Priority' },
    { field: 'status', headerName: 'Status', renderValue: (v) => STATUS_PROGRESS[v]?.label || v },
    { field: 'reported_by_name', headerName: 'Reported By' },
    { field: 'assigned_to_name', headerName: 'Assigned To' },
    { field: 'reported_at', headerName: 'Reported', renderValue: (v) => v ? formatDate(v) : '' },
    { field: 'description', headerName: 'Description' },
  ], [])

  // Keep refs in sync
  useEffect(() => { filtersRef.current = filters }, [filters])
  useEffect(() => { searchTermRef.current = searchTerm }, [searchTerm])
  useEffect(() => { pageSizeRef.current = pageSize }, [pageSize])

  const fetchReports = useCallback(async (currentPage, currentPageSize, search) => {
    try {
      setLoading(true)
      const currentFilters = filtersRef.current
      const params = {
        page: currentPage + 1,
        page_size: currentPageSize,
      }
      if (search) params.search = search
      if (currentFilters.status) params.status = currentFilters.status
      if (currentFilters.priority) params.priority = currentFilters.priority

      const response = await api.get('/assets/fault-reports/', { params })
      setReports(response.data.results || response.data)
      setTotalCount(response.data.count || 0)
    } catch (error) {
      console.error('Error fetching fault reports:', error)
      toast.error('Failed to load fault reports')
    } finally {
      setLoading(false)
    }
  }, [])

  const handlePaginationChange = useCallback(({ page: newPage, pageSize: newPageSize }) => {
    setPage(newPage)
    setPageSize(newPageSize)
    fetchReports(newPage, newPageSize, searchTermRef.current)
  }, [fetchReports])

  const handleSearchFn = useCallback((search) => {
    setSearchTerm(search)
    setPage(0)
    fetchReports(0, pageSizeRef.current, search)
  }, [fetchReports])

  // Initial load and when filters change
  useEffect(() => {
    setPage(0)
    setSearchTerm('')
    fetchReports(0, pageSizeRef.current, '')
  }, [filters, fetchReports])

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }))
  }

  const clearFilters = () => {
    setFilters({ status: '', priority: '' })
  }

  const hasActiveFilters = filters.status || filters.priority

  const getActiveFilterLabels = () => {
    const labels = {}
    if (filters.status) labels.Status = STATUS_PROGRESS[filters.status]?.label || filters.status
    if (filters.priority) labels.Priority = filters.priority
    return labels
  }

  const handleViewDetail = (report) => {
    setSelectedReport(report)
    setDetailModalOpen(true)
  }

  const handleAcknowledge = async (report) => {
    try {
      await api.post(`/assets/fault-reports/${report.id}/acknowledge/`)
      toast.success('Fault report acknowledged')
      fetchReports(page, pageSize, searchTerm)
    } catch (error) {
      toast.error('Failed to acknowledge fault report')
    }
  }

  const handleOpenAssign = (report) => {
    setSelectedReport(report)
    setAssignedTo(report.assigned_to || '')
    setAssignModalOpen(true)
  }

  const handleAssign = async () => {
    if (!assignedTo) {
      toast.error('Please select a user')
      return
    }
    setSaving(true)
    try {
      await api.post(`/assets/fault-reports/${selectedReport.id}/assign/`, {
        assigned_to: assignedTo,
      })
      toast.success('Fault report assigned')
      setAssignModalOpen(false)
      fetchReports(page, pageSize, searchTerm)
    } catch (error) {
      toast.error('Failed to assign fault report')
    } finally {
      setSaving(false)
    }
  }

  const handleOpenCreateWO = (report) => {
    setSelectedReport(report)
    // Pre-populate form with fault report data
    setWoFormData({
      title: `Fault: ${report.title}`,
      description: report.description || '',
      priority: report.priority || 'medium',
      category: 'biomedical',
      work_type: 'corrective',
      assigned_to: report.assigned_to || '',
      due_date: '',
    })
    setCreateWOModalOpen(true)
  }

  const handleCreateWorkOrder = async () => {
    setSaving(true)
    try {
      // Build payload with non-empty fields
      const payload = {}
      if (woFormData.title) payload.title = woFormData.title
      if (woFormData.description) payload.description = woFormData.description
      if (woFormData.priority) payload.priority = woFormData.priority
      if (woFormData.category) payload.category = woFormData.category
      if (woFormData.work_type) payload.work_type = woFormData.work_type
      if (woFormData.assigned_to) payload.assigned_to = woFormData.assigned_to
      if (woFormData.due_date) payload.due_date = woFormData.due_date

      const response = await api.post(`/assets/fault-reports/${selectedReport.id}/create_work_order/`, payload)
      toast.success('Work order created successfully')
      setCreateWOModalOpen(false)
      fetchReports(page, pageSize, searchTerm)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create work order')
    } finally {
      setSaving(false)
    }
  }

  const handleResolve = async (report) => {
    try {
      await api.post(`/assets/fault-reports/${report.id}/resolve/`)
      toast.success('Fault report resolved')
      fetchReports(page, pageSize, searchTerm)
    } catch (error) {
      toast.error('Failed to resolve fault report')
    }
  }

  const columns = [
    {
      field: 'report_number',
      headerName: 'Report #',
      sortable: true,
      width: 100,
    },
    {
      field: 'title',
      headerName: 'Fault Description',
      sortable: true,
    },
    {
      field: 'asset_name',
      headerName: 'Item',
      renderCell: (value, row) => (
        <Box>
          <Typography variant="body2">{value}</Typography>
          {row.asset_tag && (
            <Typography variant="caption" color="text.secondary">
              #{row.asset_tag}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 100,
      renderCell: (value) => (
        <Chip
          label={value}
          size="small"
          color={priorityColors[value] || 'default'}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Progress',
      width: 160,
      renderCell: (value) => <StatusProgressBar status={value} />,
    },
    {
      field: 'reported_by_name',
      headerName: 'Reported By',
    },
    {
      field: 'assigned_to_name',
      headerName: 'Assigned To',
      renderCell: (value) => value || '-',
    },
    {
      field: 'reported_at',
      headerName: 'Reported',
      renderCell: (value) => formatDate(value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: (_, row) => (
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleViewDetail(row)
            }}
            title="View Details"
          >
            <Visibility fontSize="small" />
          </IconButton>
          {canManage && row.status === 'reported' && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                handleAcknowledge(row)
              }}
              title="Acknowledge"
              color="primary"
            >
              <CheckCircle fontSize="small" />
            </IconButton>
          )}
          {canManage && !['resolved', 'closed', 'cancelled'].includes(row.status) && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                handleOpenAssign(row)
              }}
              title="Assign"
              color="info"
            >
              <Person fontSize="small" />
            </IconButton>
          )}
          {canManage && !row.work_order && !['resolved', 'closed', 'cancelled'].includes(row.status) && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                handleOpenCreateWO(row)
              }}
              title="Create Work Order"
              color="warning"
            >
              <Assignment fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ]

  return (
    <Box>
      <PageHeader
        title="Fault Reports"
        subtitle="View and manage equipment fault reports"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fault Reports' },
        ]}
        actions={
          <>
            <ExportButtons
              data={reports}
              columns={exportColumns}
              title="Fault Reports"
              filename="fault_reports"
              filters={getActiveFilterLabels()}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              color={hasActiveFilters ? 'primary' : 'inherit'}
            >
              Filters {hasActiveFilters && `(${Object.values(filters).filter(Boolean).length})`}
            </Button>
            <Button
              startIcon={<ReportProblem />}
              onClick={() => setReportFormOpen(true)}
              color="error"
            >
              Report a Fault
            </Button>
          </>
        }
      />

      {/* Filters Section */}
      {showFilters && (
        <Card sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={handleFilterChange('status')}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="reported">Reported</MenuItem>
                <MenuItem value="acknowledged">Acknowledged</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="work_order_created">Work Order Created</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                onChange={handleFilterChange('priority')}
                label="Priority"
              >
                <MenuItem value="">All Priorities</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>

            {hasActiveFilters && (
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                size="small"
              >
                Clear
              </Button>
            )}
          </Stack>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
              {Object.entries(getActiveFilterLabels()).map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${value}`}
                  size="small"
                  onDelete={() => {
                    const fieldMap = { 'Status': 'status', 'Priority': 'priority' }
                    setFilters(prev => ({ ...prev, [fieldMap[key]]: '' }))
                  }}
                />
              ))}
            </Stack>
          )}
        </Card>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={reports}
          loading={loading}
          searchPlaceholder="Search fault reports..."
          emptyMessage="No fault reports found"
          onRowClick={handleViewDetail}
          serverSide
          totalCount={totalCount}
          page={page}
          rowsPerPage={pageSize}
          onPaginationChange={handlePaginationChange}
          onSearch={handleSearchFn}
        />
      </Card>

      {/* Fault Report Form */}
      <FaultReportForm
        open={reportFormOpen}
        onClose={() => setReportFormOpen(false)}
        onSuccess={() => fetchReports(page, pageSize, searchTerm)}
      />

      {/* Detail Modal */}
      <Modal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={`Fault Report: ${selectedReport?.report_number || ''}`}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDetailModalOpen(false)}>
              Close
            </Button>
            {canManage && selectedReport && !['resolved', 'closed', 'cancelled'].includes(selectedReport.status) && (
              <Button color="success" onClick={() => { setDetailModalOpen(false); handleResolve(selectedReport); }}>
                Resolve
              </Button>
            )}
          </>
        }
      >
        {selectedReport && (
          <Grid container spacing={2}>
            {/* Progress Bar at top */}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Progress</Typography>
              <Box mt={1}>
                <StatusProgressBar status={selectedReport.status} />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Fault Description</Typography>
              <Typography variant="body1">{selectedReport.title}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Item</Typography>
              <Typography variant="body1">{selectedReport.asset_name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Priority</Typography>
              <Box>
                <Chip
                  label={selectedReport.priority}
                  size="small"
                  color={priorityColors[selectedReport.priority] || 'default'}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box>
                <StatusChip status={selectedReport.status} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Description</Typography>
              <Typography variant="body1">{selectedReport.description}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Reported By</Typography>
              <Typography variant="body1">{selectedReport.reported_by_name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Reported At</Typography>
              <Typography variant="body1">
                {formatDateTime(selectedReport.reported_at)}
              </Typography>
            </Grid>
            {selectedReport.assigned_to_name && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                <Typography variant="body1">{selectedReport.assigned_to_name}</Typography>
              </Grid>
            )}
            {selectedReport.work_order && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Work Order</Typography>
                <Typography variant="body1">{selectedReport.work_order_number || `WO #${selectedReport.work_order}`}</Typography>
              </Grid>
            )}
            {selectedReport.resolution_notes && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Resolution Notes</Typography>
                <Typography variant="body1">{selectedReport.resolution_notes}</Typography>
              </Grid>
            )}
            {/* Images */}
            {(selectedReport.image_1 || selectedReport.image_2 || selectedReport.image_3 || selectedReport.image_4) && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Images</Typography>
                <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                  {[selectedReport.image_1, selectedReport.image_2, selectedReport.image_3, selectedReport.image_4]
                    .filter(Boolean)
                    .map((img, idx) => (
                      <Box
                        key={idx}
                        component="img"
                        src={img}
                        alt={`Fault image ${idx + 1}`}
                        sx={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 1,
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(img, '_blank')}
                      />
                    ))}
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Modal>

      {/* Assign Modal */}
      <Modal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        title="Assign Fault Report"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setAssignModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssign} loading={saving}>
              Assign
            </Button>
          </>
        }
      >
        <AsyncSearchSelect
          endpoint="/common/users/"
          value={assignedTo}
          onChange={(id) => setAssignedTo(id)}
          getOptionLabel={(user) => {
            const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || 'Unknown'
            return user.email ? `${name} (${user.email})` : name
          }}
          label="Assign To"
        />
      </Modal>

      {/* Create Work Order Modal */}
      <Modal
        open={createWOModalOpen}
        onClose={() => setCreateWOModalOpen(false)}
        title="Create Work Order from Fault Report"
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setCreateWOModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateWorkOrder} loading={saving}>
              Create Work Order
            </Button>
          </>
        }
      >
        {selectedReport && (
          <Box>
            <Box mb={3} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2">
                <strong>Fault Report:</strong> {selectedReport.report_number}
              </Typography>
              <Typography variant="body2">
                <strong>Asset:</strong> {selectedReport.asset_name}
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={woFormData.title}
                  onChange={(e) => setWoFormData(prev => ({ ...prev, title: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={woFormData.description}
                  onChange={(e) => setWoFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={woFormData.priority}
                    onChange={(e) => setWoFormData(prev => ({ ...prev, priority: e.target.value }))}
                    label="Priority"
                  >
                    <MenuItem value="critical">Critical</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={woFormData.category}
                    onChange={(e) => setWoFormData(prev => ({ ...prev, category: e.target.value }))}
                    label="Category"
                  >
                    <MenuItem value="mechanical">Mechanical</MenuItem>
                    <MenuItem value="electrical">Electrical</MenuItem>
                    <MenuItem value="biomedical">Biomedical</MenuItem>
                    <MenuItem value="it">IT/ICT</MenuItem>
                    <MenuItem value="facilities">Facilities</MenuItem>
                    <MenuItem value="hvac">HVAC</MenuItem>
                    <MenuItem value="plumbing">Plumbing</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Work Type</InputLabel>
                  <Select
                    value={woFormData.work_type}
                    onChange={(e) => setWoFormData(prev => ({ ...prev, work_type: e.target.value }))}
                    label="Work Type"
                  >
                    <MenuItem value="corrective">Corrective Maintenance (CM/Repair)</MenuItem>
                    <MenuItem value="preventive">Preventive Maintenance (PM)</MenuItem>
                    <MenuItem value="setup_installation">Set-up/Installation</MenuItem>
                    <MenuItem value="user_training">User Training</MenuItem>
                    <MenuItem value="predictive">Predictive</MenuItem>
                    <MenuItem value="emergency">Emergency</MenuItem>
                    <MenuItem value="inspection">Inspection</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <AsyncSearchSelect
                  endpoint="/common/users/"
                  value={woFormData.assigned_to}
                  onChange={(id) => setWoFormData(prev => ({ ...prev, assigned_to: id }))}
                  getOptionLabel={(user) => {
                    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || 'Unknown'
                    return user.email ? `${name} (${user.email})` : name
                  }}
                  label="Assign To"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  value={woFormData.due_date}
                  onChange={(e) => setWoFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Modal>
    </Box>
  )
}

export default FaultReportsList
